import { AnimatePresence, motion } from "framer-motion";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useRef, useState, useMemo } from "react";
import { FiFolder, FiPaperclip, FiSend, FiSettings } from "react-icons/fi";
import InputEmoji from "react-input-emoji";
import { toast } from "react-toastify";
import { useWebSocket } from "../context/WebSocketContext";
import useClickOutside from "../hooks/useClickOutside";
import EndSessionModal from "./EndSessionModal";
import { Button, ConfigDropdown, DisabledNotice, Footer, FooterLeft } from "./styles/ChatStyles";
import { debugLog } from "../utils/debug";

const ChatFooter = ({ exportRef, isDisabled }) => {
  const emojiOptions = [
    { emoji: "😍", label: "5 - Ótimo", value: "5" },
    { emoji: "😃", label: "4 - Gostei", value: "4" },
    { emoji: "😐", label: "3 - Regular", value: "3" },
    { emoji: "😟", label: "2 - Achei ruim", value: "2" },
    { emoji: "😠", label: "1 - Péssimo", value: "1" },
  ];

  const {
    socket,
    setMessages,
    showEndModal,
    setShowEndModal,
    chatEnded,
    messages,
    clientId,
    clientName,
    transData,
    emAtendimento,
  } = useWebSocket();

  const [input, setInput] = useState("");
  const [, setIsTyping] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  useClickOutside(dropdownRef, () => setShowDropdown(false));

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const enrichData = (data = {}) => ({
    ...data,
    channel: "WEB",
    clientId,
    clientName,
    transData: {
      ...transData,
      ...(transData?.produto ? {} : searchParams.get("produto") ? { produto: searchParams.get("produto") } : {}),
      ...(transData?.area ? {} : searchParams.get("area") ? { area: searchParams.get("area") } : {}),
      clientId,
      clientName,
    },
  });

  const sendPayload = (type, data) => {
    const enrichedData = enrichData(data);

    debugLog("📤 Enviando payload:", { type, enrichedData });

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type, data: enrichedData }));
    } else {
      toast.warn("🚫 WebSocket não está conectado.", { autoClose: 6000 });
    }
  };

  const sendMessage = () => {
    if (!input.trim()) return;

    if (input.length > 1000) {
      toast.warn("⚠️ Mensagem muito longa. Máximo de 1000 caracteres.", { autoClose: 6000 });
      return;
    }

    debugLog("✅ Mensagem enviada:", input);

    const nowTime = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const userMessage = {
      from: "user",
      text: input.trimStart(),
      time: nowTime,
    };

    sendPayload("send_message", { texto: input.trim() });
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsTyping(false);
  };

  const handleAttach = () => {
    setShowDropdown(false);
    fileInputRef.current?.click();
  };

  const handleFileUpload = (file) => {
    if (!file) return;
    //const UPLOAD_BLOCK_ID = "upload-restricted";

    if (!emAtendimento) {
      toast.warn(
        "🚫 Envio de arquivos só é permitido durante o atendimento com um agente.",
        { autoClose: 6000 }
      );
      return;
    }

    const safeFilename = file.name
      .replace(/,/g, "_")
      .replace(/\s+/g, " ")
      .trim();

    if (safeFilename !== file.name) {
      toast.info("ℹ️ Ajustando nome do arquivo para envio.");
    }

    if (file.size <= 0) {
      const errorMsg = `❌ Arquivo inválido ou vazio: ${safeFilename}`;
      toast.error(errorMsg);
      return;
    }

    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      const errorMsg = `❌ Arquivo muito grande: ${safeFilename} (${(
        file.size /
        1024 /
        1024
      ).toFixed(2)} MB). Limite: 5 MB.`;
      toast.error(errorMsg);
      setMessages((prev) => [
        ...prev,
        {
          from: "system",
          text: errorMsg,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
      return;
    }

    const allowedExtensions = {
      FILE_IMAGE: ["jpg", "jpeg", "png", "bmp"],
      FILE: ["mp3", "aac", "mp4", "txt", "csv", "pdf"],
    };

    const extension = (safeFilename.split(".").pop() || "").toLowerCase();
    const mediaType = Object.entries(allowedExtensions).find(([, exts]) =>
      exts.includes(extension)
    )?.[0];

    if (!mediaType) {
      const errorMsg = `❌ Tipo de arquivo não permitido: ${safeFilename}`;
      toast.error(errorMsg);
      setMessages((prev) => [
        ...prev,
        {
          from: "system",
          text: errorMsg,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
      return;
    }

    debugLog("📎 Arquivo válido:", {
      name: safeFilename,
      extension,
      mediaType,
    });

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result).split(",")[1];

      sendPayload("chat_media", {
        filename: safeFilename,
        mediaType,
        extension,
        base64,
        fileSize: file.size,
        from: "user",
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      });

      toast.success(`📎 Arquivo enviado com sucesso: ${safeFilename}`);
      setMessages((prev) => [
        ...prev,
        {
          from: "system",
          text: `📎 Arquivo enviado com sucesso: ${safeFilename}`,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    };

    reader.onerror = () => {
      toast.error("❌ Erro ao carregar o arquivo. Tente novamente.");
    };

    reader.readAsDataURL(file);
  };

  const handleDownload = () => {
    try {
      debugLog("📄 Iniciando geração do PDF do histórico...");
      const doc = new jsPDF();
      const nm =
        new URLSearchParams(window.location.search)
          .get("clientName")
          ?.replace(/\./g, " ") || "Usuário";

      doc.setFontSize(14);
      doc.text("Histórico da Conversa", 14, 20);
      doc.setFontSize(12);
      doc.text(`Usuário: ${nm}`, 14, 28);

      const rows = messages.flatMap((msg) => {
        const text = sanitizeTextContent(msg.text);
        const lines = text.split(/\r?\n/);
        return lines.map((line, i) => [
          i === 0 ? msg.time || "--:--" : "",
          i === 0 ? getAuthorName(msg) : "",
          line,
        ]);
      });

      autoTable(doc, {
        startY: 35,
        head: [["Data/Hora", "Autor", "Mensagem"]],
        body: rows,
        styles: { fontSize: 10, cellPadding: 4, lineHeight: 1.3 },
        headStyles: { fillColor: [41, 98, 255], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 14, right: 14 },
      });

      doc.save(`chat-historico-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (err) {
      toast.error("❌ Erro ao gerar o PDF do histórico.");
    }
  };

  const decodeUnicodeSafely = (str) => {
    try {
      return JSON.parse(`"${str}"`);
    } catch {
      return str;
    }
  };

  const sanitizeTextContent = (text) => {
    if (!text) return "";
    let cleaned = decodeUnicodeSafely(text);
    return cleaned
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/?[^>]+(>|$)/g, "")
      .replace(/[^\x20-\x7EÀ-ÿ\r\n]/g, "")
      .trim();
  };

  const getAuthorName = (msg) => {
    const from = (msg.from || "").toLowerCase();
    const nickname = msg.senderNickname?.toLowerCase();
    const source = (msg.sourceType || "").toUpperCase();

    if (
      from === "system" ||
      from === "bot" ||
      source === "ROBOT" ||
      source === "SYSTEM" ||
      nickname === "sistema" ||
      nickname === "sofi"
    )
      return "Sofi";

    if (source === "AGENT" && msg.senderNickname) return msg.senderNickname;
    if (from === "agent" && msg.senderNickname) return msg.senderNickname;

    return "Você";
  };

  return (
    <Footer>
      <FooterLeft>
        <div>
          <button
            title="Encerrar Atendimento"
            onClick={() => setShowEndModal(true)}
          />
        </div>
        <div style={{ position: "relative" }}>
          <button
            title="Mais ações"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <FiSettings />
          </button>
          <AnimatePresence>
            {showDropdown && (
              <motion.div
                ref={dropdownRef}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
                style={{ position: "absolute", top: "-110px", left: 0, zIndex: 100 }}
              >
                <ConfigDropdown as="div">
                  <button onClick={handleAttach}>
                    <FiPaperclip /> Anexar Arquivo
                  </button>
                  <button onClick={handleDownload}>
                    <FiFolder /> Baixar Histórico
                  </button>
                </ConfigDropdown>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </FooterLeft>

      <div style={{ position: "relative", width: "100%" }}>
        <InputEmoji
          value={input}
          onChange={(text) => {
            if (text.length <= 1000) setInput(text);
            else toast.warn("⚠️ Limite de 1000 caracteres atingido.");
          }}
          cleanOnEnter
          onEnter={sendMessage}
          placeholder="Digite sua mensagem aqui..."
          fontFamily="inherit"
          borderRadius={16}
          disabled={isDisabled}
          style={{ paddingRight: "80px" }}
        />

        <span
          style={{
            position: "absolute",
            bottom: "10px",
            right: "46px",
            fontSize: "11px",
            color: input.length > 1000 ? "red" : "#999",
            background: "#fff",
            padding: "0 2px",
            borderRadius: "4px",
            pointerEvents: "none",
          }}
        >
          {input.length}/1000
        </span>
      </div>

      <Button onClick={sendMessage} disabled={isDisabled} style={{ opacity: isDisabled ? 0.5 : 1 }}>
        <span className="hide-on-mobile">ENVIAR</span>
        <span className="show-on-mobile"><FiSend /></span>
      </Button>

      {chatEnded && <DisabledNotice>Atendimento encerrado. Obrigado pelo contato.</DisabledNotice>}

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".jpg,.jpeg,.png,.bmp,.mp3,.aac,.mp4,.txt,.csv,.pdf,application/pdf"
        onChange={(e) => handleFileUpload(e.target.files[0])}
      />

      <EndSessionModal
        show={showEndModal}
        onClose={() => setShowEndModal(false)}
        onDownload={handleDownload}
        onSubmit={(type, payload) => {
          sendPayload(type, payload);
          const emoji = emojiOptions.find(opt => opt.value === payload.evaluation)?.emoji || "⭐";
          setMessages((prev) => [
            ...prev,
            {
              from: "system",
              text: `📄 Avaliação registrada: ${emoji}`,
              time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              timestamp: Date.now(),
            },
          ]);
        }}
      />
    </Footer>
  );
};

export default ChatFooter;